// src/pages/FormationsPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { formationService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Styled components
const FormationsSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.text + '50'};
  border-radius: 30px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  }
`;

const FormationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormationCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: ${props => props.theme.card.hover};
  }
`;

const FormationImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: contain; 
  background-repeat: no-repeat; 
  background-position: center; 
  position: relative;
`;

const OnlineTag = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.primary};
`;

const FormationContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FormationTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.primary};
    }
  }
`;

const FormationMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  
  div {
    display: flex;
    align-items: center;
    
    i {
      width: 20px;
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const FormationDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const FormationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.text}10;
`;

const FormationPrice = styled.div`
  font-weight: bold;
  color: ${props => props.theme.primary};
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.active ? props.theme.primary : props.theme.card.background};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.text + '20'};
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Filtres prédéfinis pour les formations
const filters = [
  { id: 'all', label: 'allFormations' },
  { id: 'upcoming', label: 'upcomingFormations' },
  { id: 'online', label: 'onlineFormations' }
];

const FormationsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState(1);
  
  // Référence pour l'animation
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Mise à jour des paramètres d'URL quand les filtres ou la pagination changent
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeFilter !== 'all') {
      params.set('filter', activeFilter);
    }
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    }
    setSearchParams(params);
  }, [activeFilter, currentPage, setSearchParams]);
  
  // Chargement des formations
  useEffect(() => {
    const fetchFormations = async () => {
      try {
        setLoading(true);
        
        // Paramètres pour l'API
        const params = {
          page: currentPage,
          page_size: 9 // Nombre de formations par page
        };
        
        // Ajout des filtres
        if (activeFilter === 'upcoming') {
          params.upcoming = true;
        } else if (activeFilter === 'online') {
          params.is_online = true;
        }
        
        const response = await formationService.getAll(params);
        
        // S'assurer que la structure de données attendue existe
        if (response.data && response.data.results) {
          setFormations(response.data.results);
          setTotalPages(Math.ceil(response.data.count / 9));
        } else {
          // Si l'API ne renvoie pas le format attendu, utiliser les données telles quelles
          setFormations(Array.isArray(response.data) ? response.data : []);
          setTotalPages(1);
        }
        
        // Mise à jour du titre de la page
        document.title = `${t('formations')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching formations:", err);
        setError(err.response?.data?.detail || t('errorLoadingFormations'));
        setFormations([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFormations();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [activeFilter, currentPage, t]);
  
  // Changement de filtre
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setCurrentPage(1); // Réinitialiser la pagination lors du changement de filtre
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Formatage du prix
  const formatPrice = (price) => {
    if (price === 0) return t('free');
    if (!price) return t('contactForPricing');
    return `${price.toLocaleString()} FCFA`;
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <FormationsSection>
      <Container>
        <SectionTitle
          title={t('formations')}
          subtitle={t('formationsSubtitle')}
        />
        
        <FilterBar>
          {filters.map(filter => (
            <FilterButton
              key={filter.id}
              theme={theme}
              active={activeFilter === filter.id}
              onClick={() => handleFilterChange(filter.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t(filter.label)}
            </FilterButton>
          ))}
        </FilterBar>
        
        {formations.length === 0 ? (
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-graduation-cap"></i>
              </div>
              <h3>{t('noFormationsFound')}</h3>
              <p>{t('checkBackLater')}</p>
            </motion.div>
          </NoResults>
        ) : (
          <>
            <FormationsGrid ref={ref}>
              {formations.map((formation, index) => (
                <motion.div
                  key={formation.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <FormationCard theme={theme}>
                    <FormationImage src={formation.image}>
                      {formation.is_online && (
                        <OnlineTag theme={theme}>{t('online')}</OnlineTag>
                      )}
                    </FormationImage>
                    
                    <FormationContent>
                      <FormationTitle theme={theme}>
                        <a href={`/formations/${formation.id}`}>{formation.title}</a>
                      </FormationTitle>
                      
                      <FormationMeta theme={theme}>
                        <div>
                          <i className="far fa-calendar-alt"></i>
                          <span>
                            {formatDate(formation.start_date)}
                            {formation.end_date && formation.end_date !== formation.start_date && 
                              ` - ${formatDate(formation.end_date)}`}
                          </span>
                        </div>
                        <div>
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{formation.is_online ? t('onlineFormation') : formation.location}</span>
                        </div>
                        {formation.instructor && (
                          <div>
                            <i className="fas fa-user"></i>
                            <span>
                              {formation.instructor.first_name} {formation.instructor.last_name}
                            </span>
                          </div>
                        )}
                        <div>
                          <i className="fas fa-users"></i>
                          <span>
                            {formation.participants ? formation.participants.length : 0} / {formation.max_participants} {t('participants')}
                          </span>
                        </div>
                      </FormationMeta>
                      
                      <FormationDescription theme={theme}>
                        {formation.short_description || formation.description.substring(0, 150)}...
                      </FormationDescription>
                      
                      <FormationFooter theme={theme}>
                        <FormationPrice theme={theme}>
                          {formatPrice(formation.price)}
                        </FormationPrice>
                        
                        <a href={`/formations/${formation.id}`} className="btn-primary btn-small">
                          {t('viewDetails')}
                        </a>
                      </FormationFooter>
                    </FormationContent>
                  </FormationCard>
                </motion.div>
              ))}
            </FormationsGrid>
            
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton 
                  theme={theme}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                </PaginationButton>
                
                {[...Array(totalPages).keys()].map(page => (
                  <PaginationButton
                    key={page + 1}
                    theme={theme}
                    active={currentPage === page + 1}
                    onClick={() => setCurrentPage(page + 1)}
                  >
                    {page + 1}
                  </PaginationButton>
                ))}
                
                <PaginationButton 
                  theme={theme}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-chevron-right"></i>
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}
      </Container>
    </FormationsSection>
  );
};

export default FormationsPage;